export type ActivityLogMarkdownTableAlign = 'left' | 'center' | 'right';

export type ActivityLogMarkdownBlock =
  | { type: 'paragraph'; html: string }
  | { type: 'ordered_list'; html: string }
  | { type: 'unordered_list'; html: string }
  | {
      type: 'table';
      headers: string[];
      alignments: ActivityLogMarkdownTableAlign[];
      rows: string[][];
    };

type MarkdownBlockType = 'ordered_list' | 'unordered_list' | 'table' | 'paragraph';

const ORDERED_LIST_ITEM_PATTERN = /^\d+\.\s+(.*)$/;
const UNORDERED_LIST_ITEM_PATTERN = /^[-*]\s+(.*)$/;
const TABLE_SEPARATOR_CELL_PATTERN = /^:?-{3,}:?$/;

function splitTableRow(line: string) {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => cell.trim());
}

function normalizeTableRow(cells: string[], columnCount: number) {
  if (cells.length === columnCount) {
    return cells;
  }
  if (cells.length > columnCount) {
    return cells.slice(0, columnCount);
  }
  return [...cells, ...Array.from({ length: columnCount - cells.length }, () => '')];
}

function isTableBlock(lines: string[]) {
  if (lines.length < 2) {
    return false;
  }

  const headerCells = splitTableRow(lines[0]);
  const separatorCells = splitTableRow(lines[1]);

  if (headerCells.length < 2 || headerCells.length !== separatorCells.length) {
    return false;
  }

  return separatorCells.every((cell) => TABLE_SEPARATOR_CELL_PATTERN.test(cell));
}

function getTableCellAlign(cell: string): ActivityLogMarkdownTableAlign {
  const startsAligned = cell.startsWith(':');
  const endsAligned = cell.endsWith(':');

  if (startsAligned && endsAligned) {
    return 'center';
  }
  if (endsAligned) {
    return 'right';
  }
  return 'left';
}

function parseTableBlock(
  lines: string[],
  renderInlineHtml: (text: string) => string
): Extract<ActivityLogMarkdownBlock, { type: 'table' }> {
  const headers = splitTableRow(lines[0]);
  const alignments = splitTableRow(lines[1]).map(getTableCellAlign);
  const columnCount = headers.length;
  return {
    type: 'table',
    headers: headers.map((cell) => renderInlineHtml(cell)),
    alignments,
    rows: lines
      .slice(2)
      .map((line) => normalizeTableRow(splitTableRow(line), columnCount))
      .map((cells) => cells.map((cell) => renderInlineHtml(cell))),
  };
}

function renderListHtml(lines: string[], ordered: boolean, renderInlineHtml: (text: string) => string) {
  const tag = ordered ? 'ol' : 'ul';
  const pattern = ordered ? ORDERED_LIST_ITEM_PATTERN : UNORDERED_LIST_ITEM_PATTERN;
  const items = lines
    .map((line) => line.match(pattern)?.[1] ?? line)
    .map((item) => `<li>${renderInlineHtml(item)}</li>`)
    .join('');
  return `<${tag}>${items}</${tag}>`;
}

function renderParagraphHtml(lines: string[], renderInlineHtml: (text: string) => string) {
  return `<p>${lines.map((line) => renderInlineHtml(line)).join('<br>')}</p>`;
}

function getBlockType(lines: string[]): MarkdownBlockType {
  if (isTableBlock(lines)) {
    return 'table';
  }

  if (lines.every((line) => UNORDERED_LIST_ITEM_PATTERN.test(line))) {
    return 'unordered_list';
  }

  if (lines.every((line) => ORDERED_LIST_ITEM_PATTERN.test(line))) {
    return 'ordered_list';
  }

  return 'paragraph';
}

export function parseActivityLogMarkdownBlocksWithInlineRenderer(
  markdown: string,
  renderInlineHtml: (text: string) => string
): ActivityLogMarkdownBlock[] {
  const normalized = markdown.replaceAll(/\r\n?/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.split('\n').filter((line) => line.trim().length > 0))
    .filter((lines) => lines.length > 0);

  const mergedBlocks = blocks.reduce<{ type: MarkdownBlockType; lines: string[] }[]>((acc, lines) => {
    const type = getBlockType(lines);
    const previous = acc.at(-1);

    if (previous && previous.type === type && type !== 'paragraph') {
      previous.lines.push(...lines);
      return acc;
    }

    acc.push({ type, lines: [...lines] });
    return acc;
  }, []);

  return mergedBlocks.map(({ type, lines }) => {
    if (type === 'unordered_list') {
      return { type, html: renderListHtml(lines, false, renderInlineHtml) } as const;
    }

    if (type === 'ordered_list') {
      return { type, html: renderListHtml(lines, true, renderInlineHtml) } as const;
    }

    if (type === 'table') {
      return parseTableBlock(lines, renderInlineHtml);
    }

    return { type: 'paragraph', html: renderParagraphHtml(lines, renderInlineHtml) } as const;
  });
}
